/**
 * CR283: Merchant Diagnostics Card Component
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, RefreshCw, CheckCircle } from "lucide-react";
import type { MerchantDirectoryRow } from "@/hooks/useMerchantsDirectory";
import { useMerchantDirectoryMutations } from "@/hooks/useMerchantsDirectory";

interface MerchantDiagnosticsCardProps {
  merchant: MerchantDirectoryRow;
}

export function MerchantDiagnosticsCard({ merchant }: MerchantDiagnosticsCardProps) {
  const { resyncLegacy, isResyncing } = useMerchantDirectoryMutations();

  const hasMismatch = merchant.scan_enabled_mismatch || merchant.hidden_mismatch;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {hasMismatch ? (
            <AlertTriangle className="h-5 w-5 text-warning" />
          ) : (
            <CheckCircle className="h-5 w-5 text-success" />
          )}
          Diagnostics
        </CardTitle>
        <CardDescription>
          Computed values vs legacy columns. Mismatches indicate drift.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          {/* Computed Values */}
          <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
            <div className="font-medium text-xs uppercase text-muted-foreground">Computed (Truth)</div>
            <div className="flex justify-between">
              <span>scan_enabled</span>
              <Badge variant={merchant.scan_enabled ? "default" : "secondary"}>
                {merchant.scan_enabled ? "true" : "false"}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span>sources_count</span>
              <span className="font-mono">{merchant.sources_count}</span>
            </div>
            <div className="flex justify-between">
              <span>active_sources_count</span>
              <span className="font-mono">{merchant.active_sources_count}</span>
            </div>
            <div className="flex justify-between">
              <span>directory_status</span>
              <Badge variant="outline">{merchant.directory_status}</Badge>
            </div>
          </div>

          {/* Legacy Values */}
          <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
            <div className="font-medium text-xs uppercase text-muted-foreground">Legacy (Display Only)</div>
            <div className="flex justify-between items-center">
              <span>scan_enabled</span>
              <div className="flex items-center gap-1">
                <span className="font-mono text-muted-foreground">
                  {merchant.legacy_scan_enabled ? "true" : "false"}
                </span>
                {merchant.scan_enabled_mismatch && (
                  <AlertTriangle className="h-4 w-4 text-warning" />
                )}
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span>is_hidden</span>
              <div className="flex items-center gap-1">
                <span className="font-mono text-muted-foreground">
                  {merchant.legacy_is_hidden ? "true" : "false"}
                </span>
                {merchant.hidden_mismatch && (
                  <AlertTriangle className="h-4 w-4 text-warning" />
                )}
              </div>
            </div>
            <div className="flex justify-between">
              <span>review_state</span>
              <span className="font-mono text-muted-foreground">
                {merchant.legacy_review_state || "—"}
              </span>
            </div>
          </div>
        </div>

        {hasMismatch && (
          <Button
            variant="outline"
            onClick={() => resyncLegacy(merchant.merchant_key)}
            disabled={isResyncing}
            className="w-full"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isResyncing ? "animate-spin" : ""}`} />
            Resync Legacy Flags
          </Button>
        )}

        {!hasMismatch && (
          <div className="text-center text-sm text-muted-foreground">
            All legacy flags are in sync with computed values.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
